import { type CMSDataQueryFunctionContext } from '@/features/cms/services/CMS/fetchers'
import { type User } from '@/features/account/services/User/types'
import { getDefaultShoppingAddressWithUser } from '@/features/account/services/User/utils'
import { type Address, type ValidAddress } from '@/features/account/services/Addresses/types'
import { type ShoppingCategory } from '@/features/shop/services/ShoppingCategories/types'
import { type CMSCatData, type TermsByState } from '@/features/cms/components/CMS/types'
import { getRetailerSearchUrl } from '@shared/constants/routes'
import { getSearchQueryString } from '@/features/shared/utils/url'
import { getCookie } from '@/features/shared/utils/cookies'
import { CMS_UTM_SOURCE } from '@/features/entrypoint/components/Circle360/constants'
import { singleValueOrNone } from '@/features/shared/serverUtils/request'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'

export const INVALID_STORE_SUPPLIED_MESSAGE =
  'Invalid store supplied for user in CMS page querystring'

export const createFetchCMSDataConfig = ({
  context,
  isOnServer,
  enableDeferredHydration,
  contentAlias,
}: {
  context: CMSDataQueryFunctionContext
  isOnServer: boolean
  enableDeferredHydration: boolean
  contentAlias?: string
}) => {
  const [
    ,
    {
      slug,
      utmParams = {},
      storeParams,
      addressParams,
      subscriptionType,
      hasPlacedMarketplaceOrder,
      inputVariables,
    },
  ] = context.queryKey

  const data = {
    slug,
    ...storeParams,
    ...addressParams,
    enable_deferred_hydration: enableDeferredHydration,
    address_id: addressParams?.address_id.toString(),
    subscription_type: subscriptionType,
    has_placed_marketplace_order: hasPlacedMarketplaceOrder,
    ...(inputVariables && { input_variables: inputVariables }),
  }
  const { headers } = context.meta ?? {}

  // Capture the visitor's IP address via the x-forwarded-for header
  // Note: the x-forwarded-for header will not be set on localhost
  const forwardedFor = singleValueOrNone(headers?.['x-forwarded-for'])
  const ip = forwardedFor?.split(',')[0] ?? ''

  const userAgent = singleValueOrNone(headers?.['user-agent']) ?? ''
  const cmsUrl = contentAlias ? `content-alias/${contentAlias}` : 'page-content'
  const utm_source = getCookie(CMS_UTM_SOURCE)

  if (utm_source) {
    utmParams['utm_source'] = utm_source
  }

  return {
    // If request is coming from server during SSR, we should send the x-real-ip header and x-real-user-agent
    ...(isOnServer && {
      headers: {
        'x-real-ip': ip,
        'x-real-user-agent': userAgent,
      },
    }),
    url: `/cms/v1/${cmsUrl}${getSearchQueryString(utmParams)}`,
    data,
    onErrorLog: { ...data },
  }
}

export const getAddressParamsForCMS = (user: User, zipForZipOnlyUser: string | undefined) => {
  const defaultAddress = getDefaultShoppingAddressWithUser(user)
  if (defaultAddress) return formatCMSAddress(defaultAddress)

  return zipForZipOnlyUser ? formatCMSAddress({ zip_code: zipForZipOnlyUser }) : undefined
}

export const getGuestAddressParamsForCMS = (guestAddress?: ValidAddress) => {
  return formatCMSAddress(guestAddress)
}

type CMSAddress =
  | {
      address_id: number
      address_city: string | undefined
      address_state: string | undefined
      address_line_1: string | undefined
      address_line_2: string | undefined
      zip: string
    }
  | undefined

// the pure body of useCMSParamsWithoutSlug, so server code can build the same
// CMS query keys (e.g. the category nav) from the same session data.
export const getCMSParamsWithoutSlug = ({
  user,
  zipForZipOnlyUser,
  storeParams,
  guestAddress,
}: {
  user: User | undefined
  zipForZipOnlyUser: string | undefined
  storeParams: StoreParams
  guestAddress: ValidAddress | undefined
}) => {
  // empty string is fallback to adhere to StoreParams type
  const zipToUse = (storeParams.zip ? storeParams.zip : zipForZipOnlyUser) ?? ''
  let addressToUse: CMSAddress
  if (user) {
    addressToUse = getAddressParamsForCMS(user, zipForZipOnlyUser)
  } else if (guestAddress) {
    addressToUse = getGuestAddressParamsForCMS(guestAddress)
  } else if (zipForZipOnlyUser) {
    addressToUse = {
      // stub out all properties, otherwise type chaos will ensue -- https://app.clickup.com/t/86b8nr65c
      zip: zipForZipOnlyUser,
      address_id: 0,
      address_city: '',
      address_line_1: '',
      address_line_2: '',
      address_state: '',
    }
  }

  return {
    storeParams: {
      ...storeParams,
      zip: zipToUse,
    } satisfies StoreParams, // ensure we adhere to StoreParams type in this return type
    addressParams: addressToUse,
    subscriptionType: user?.subscription?.type ?? undefined,
    hasPlacedMarketplaceOrder: user?.has_placed_marketplace_order,
  }
}

export const formatCMSAddress = (address?: Address | ValidAddress) => {
  if (!address) return undefined

  const addressId = ('id' in address && address.id) || 0

  return {
    address_id: addressId,
    address_city: address.city,
    address_state: address.state,
    address_line_1: address.street1,
    address_line_2: address.street2,
    zip: address.zip_code,
  }
}

const mapCategoryChildren = (children?: CMSCatData[] | null) => children?.map(toCategory)

export const toCategory = (category: CMSCatData): ShoppingCategory => {
  const {
    data: {
      heading,
      image,
      children,
      link: { url, type },
    },
    metadata,
  } = category

  return {
    ...metadata.read_operation_info.source_object?.object,
    name: heading,
    image_url: image?.src,
    relativeLink: type === 'relative' ? url : undefined,
    children: mapCategoryChildren(children),
  }
}

export const getCategoryUrl = (category: ShoppingCategory, storeName?: string) => {
  const { id = 0, relativeLink } = category
  return relativeLink ?? `${getRetailerSearchUrl(storeName)}?category_id=${id}`
}

export const getStateTermData = (termsByState: TermsByState[], userState?: string) =>
  termsByState.find((term) => term.data.state === userState)
